/**
 * Foodleap public-page analytics
 *
 * Three layers work together:
 *  1. Virtual page views   — essential for SPA; each route fires its own trackPageView
 *  2. Content impressions  — Matomo's built-in content tracking reports view-to-click rates
 *                            per shift card / product card / pricing tier automatically
 *  3. Scroll depth         — 25/50/75/100% milestones reveal drop-off points per page
 *
 * Usage (Options API):
 *   import { trackFoodleapPageView, createScrollDepthTracker, ... } from '@/util/useFoodleapTracking'
 *
 *   mounted() {
 *     trackFoodleapPageView('/foodleap', 'Foodleap - Home')
 *     this._scrollTracker = createScrollDepthTracker('Foodleap Home')
 *     this._scrollTracker.start()
 *   },
 *   beforeUnmount() {
 *     this._scrollTracker?.stop()
 *   }
 */

import { trackMatomoEvent } from './analytics.js'
import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '../constants/analytics-constants.js'

const _paq = () => (window._paq = window._paq || [])

// ── Virtual page view ────────────────────────────────────────────────────────
// Must be called on every SPA route mount; otherwise Matomo counts all pages
// under the initial URL and session data is completely wrong.

export function trackFoodleapPageView (path, title) {
  _paq().push(['setCustomUrl', path])
  _paq().push(['setDocumentTitle', title])
  _paq().push(['trackPageView'])
}

// ── Scroll depth ─────────────────────────────────────────────────────────────
// Returns { start, stop } so the component can clean up in beforeUnmount.
// Fires once per milestone per page load (Set prevents double-firing).

export function createScrollDepthTracker (pageName) {
  const milestones = [25, 50, 75, 100]
  const fired = new Set()

  const handler = () => {
    const pct = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    )
    milestones.forEach(m => {
      if (pct >= m && !fired.has(m)) {
        fired.add(m)
        trackMatomoEvent(
          MATOMO_EVENT_CATEGORIES.FOODLEAP_ENGAGEMENT,
          MATOMO_EVENT_ACTIONS.SCROLL_DEPTH,
          `${pageName} ${m}%`
        )
      }
    })
  }

  return {
    start () { window.addEventListener('scroll', handler, { passive: true }) },
    stop () { window.removeEventListener('scroll', handler) },
  }
}

// ── Content impressions & interactions ───────────────────────────────────────
// Matomo's content tracking (separate from Events) gives view-to-click rates
// that show up under Reports › Contents.
//
// Pattern:
//   trackFoodleapImpression('Foodleap Shift Card', 'Protein Diversification')   ← when visible
//   trackFoodleapInteraction('Foodleap Shift Card', 'Protein Diversification')  ← on click

export function trackFoodleapImpression (contentName, piece) {
  _paq().push(['trackContentImpression', contentName, piece, window.location.pathname])
}

export function trackFoodleapInteraction (contentName, piece, interaction = 'click') {
  _paq().push(['trackContentInteraction', interaction, contentName, piece, window.location.pathname])
}

// ── Section visibility ────────────────────────────────────────────────────────
// Fires when a named section scrolls into view.
// Shows which parts of the page users actually reach (beyond scroll depth %).

export function trackFoodleapSection (pageName, sectionName) {
  trackMatomoEvent(
    MATOMO_EVENT_CATEGORIES.FOODLEAP_ENGAGEMENT,
    MATOMO_EVENT_ACTIONS.SECTION_VIEW,
    `${pageName} › ${sectionName}`
  )
}

// ── CTA clicks ────────────────────────────────────────────────────────────────
// Full context in the event name so funnels are readable without custom dims.
// Format: "Page › Section › Label"  or  "Page › Label"

export function trackFoodleapCta (pageName, label, section = null) {
  const name = section
    ? `${pageName} › ${section} › ${label}`
    : `${pageName} › ${label}`
  trackMatomoEvent(
    MATOMO_EVENT_CATEGORIES.FOODLEAP_ENGAGEMENT,
    MATOMO_EVENT_ACTIONS.CTA_CLICK,
    name
  )
}

// ── Impression observer factory ───────────────────────────────────────────────
// Creates an IntersectionObserver that fires a content impression the first
// time each observed element enters the viewport.
// Elements must carry data-content-name and data-content-piece attributes.
// Returns the observer so the component can disconnect() in beforeUnmount.

export function createImpressionObserver (threshold = 0.5) {
  let observer
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const { contentName, contentPiece } = entry.target.dataset
          if (contentName && contentPiece) {
            trackFoodleapImpression(contentName, contentPiece)
          }
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold }
  )
  return observer
}
